import { useMemo, useState } from 'react';
import { useCompaniesData } from '../../../../../Hooks/TT_ELD/Companies';
import CompaniesTable from './CompaniesTable';
import { Button, Drawer, Grid, Select, Space } from 'antd';
import { FilterOutlined } from '@ant-design/icons';
import SearchInput from '../../../../../Utils/SearchInput';
import useRouteQuery from '../../../../../Utils/useRouterQuery';
import { useTagQuerySync } from '../../../../../Utils/useTagQuerySync';
import MultipleEditModal from './Company/MultipleEditModal';
import { getUserFromLocalStorage } from '../../../../../Utils/sidebar';
import { isWhiteLabelAdmin } from '../../../../../Utils/whiteLabel';

const { useBreakpoint } = Grid;

const Companies = () => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const isWhiteLabel = isWhiteLabelAdmin();

  const [usdotId, setUsdotId] = useRouteQuery('', 'usdotId', (v) => String(v));
  const [uid, setUid] = useRouteQuery('', 'uid', (v) => String(v));
  const [companyName, setCompanyName] = useRouteQuery('', 'companyName', String);
  const [stripeCustomerId, setStripeCustomerId] = useRouteQuery('', 'stripeCustomerId', (v) =>
    String(v),
  );
  const [publicId, setPublicId] = useRouteQuery('', 'publicId', (v) => String(v));
  const [isContract, setIsContract] = useState<boolean | undefined>(undefined);
  const [skip, setSkip] = useState<string>('1');

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const user = getUserFromLocalStorage();

  const canSeeStripeCustomerId =
    (user?.role === 'developer' || user?.role === 'manager' || user?.role === 'sales') &&
    !isWhiteLabel;

  const canAddChatId =
    user?.role === 'developer' ||
    user?.role === 'manager' ||
    user?.role === 'support' ||
    user?.role === 'support_manager';

  const handleChange = (event: string, inputName: string) => {
    const updateFunctions: Record<string, (value: string) => void> = {
      companyName: setCompanyName,
      usdotId: setUsdotId,
      uid: setUid,
      stripeCustomerId: setStripeCustomerId,
      publicId: setPublicId,
    };

    if (updateFunctions[inputName]) {
      updateFunctions[inputName](event.trim());
    }
  };

  const { data, refetch, isLoading, isFetching } = useCompaniesData(
    companyName,
    usdotId,
    undefined,
    skip,
    '',
    '',
    '',
    uid,
    undefined,
    stripeCustomerId,
    publicId,
    isContract,
  );

  useTagQuerySync(
    { en_US: 'Companies', zh_CN: '' },
    'companies',
    { companyName, uid, usdotId, skip, stripeCustomerId, publicId },
    {
      companyName: setCompanyName,
      uid: setUid,
      usdotId: setUsdotId,
      skip: setSkip,
      stripeCustomerId: setStripeCustomerId,
      publicId: setPublicId,
    },
  );

  const onChange = (query: any) => setSkip(query.current);

  const updates = () => {
    refetch();
  };

  const filtersUI = useMemo(
    () => (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
        <SearchInput
          value={companyName}
          placeholder="Search by company"
          onDebounceChange={(v: string) => handleChange(v, 'companyName')}
          searchWidth="100%"
        />

        <SearchInput
          value={publicId}
          placeholder="Search by Public ID"
          onDebounceChange={(event: string) => handleChange(event, 'publicId')}
          searchWidth="100%"
        />

        <SearchInput
          value={usdotId}
          placeholder="Search by USDOT"
          onDebounceChange={(v: string) => handleChange(v, 'usdotId')}
          searchWidth="100%"
        />

        {!isWhiteLabel && (
          <SearchInput
            value={uid}
            placeholder="Search by UID"
            onDebounceChange={(v: string) => handleChange(v, 'uid')}
            searchWidth="100%"
          />
        )}

        {canSeeStripeCustomerId && (
          <SearchInput
            value={stripeCustomerId}
            placeholder="Search by Stripe Customer ID"
            onDebounceChange={(v: string) => handleChange(v, 'stripeCustomerId')}
            searchWidth="100%"
          />
        )}

        <Select
          allowClear
          placeholder="Contract"
          style={{ width: '100%' }}
          value={isContract}
          onChange={(v: boolean | undefined) => setIsContract(v)}
          options={[
            { label: 'Contract: True', value: true },
            { label: 'Contract: False', value: false },
          ]}
        />

        {isMobile && (
          <Space style={{ width: '100%' }} size={10}>
            <Button block onClick={() => setFiltersOpen(false)}>
              Close
            </Button>
            <Button
              block
              type="primary"
              onClick={() => {
                updates();
                setFiltersOpen(false);
              }}
            >
              Apply & Refresh
            </Button>
          </Space>
        )}
      </div>
    ),
    [companyName, usdotId, uid, stripeCustomerId, isMobile, canSeeStripeCustomerId],
  );

  return (
    <div className="mainPage">
      {/* Toolbar */}
      {!isMobile ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            marginBottom: 10,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 8,
            }}
          >
            <SearchInput
              value={companyName}
              placeholder="Search by company"
              onDebounceChange={(v: string) => handleChange(v, 'companyName')}
            />
            <SearchInput
              value={publicId}
              placeholder="Search by Public ID"
              onDebounceChange={(event: string) => handleChange(event, 'publicId')}
            />
            <SearchInput
              value={usdotId}
              placeholder="Search by USDOT"
              onDebounceChange={(v: string) => handleChange(v, 'usdotId')}
            />
            {!isWhiteLabel && (
              <SearchInput
                value={uid}
                placeholder="Search by UID"
                onDebounceChange={(v: string) => handleChange(v, 'uid')}
              />
            )}

            {canSeeStripeCustomerId && (
              <SearchInput
                value={stripeCustomerId}
                placeholder="Search by Stripe Customer ID"
                onDebounceChange={(v: string) => handleChange(v, 'stripeCustomerId')}
              />
            )}

            <Select
              allowClear
              placeholder="Contract"
              style={{ width: 160 }}
              value={isContract}
              onChange={(v: boolean | undefined) => setIsContract(v)}
              options={[
                { label: 'Contract: True', value: true },
                { label: 'Contract: False', value: false },
              ]}
            />
          </div>

          {/* Desktop actions right */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {canAddChatId && (
              <Button size="large" type="primary" onClick={() => setIsModalVisible(true)}>
                Add Chat ID
              </Button>
            )}
            <Button size="large" onClick={updates}>
              Refresh
            </Button>
          </div>
        </div>
      ) : (
        // Mobile actions row
        <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
          <Button
            block
            size="large"
            icon={<FilterOutlined />}
            onClick={() => setFiltersOpen(true)}
          />
          {canAddChatId && (
            <Button size="large" block type="primary" onClick={() => setIsModalVisible(true)}>
              Add Chat ID
            </Button>
          )}
          <Button block size="large" onClick={updates}>
            Refresh
          </Button>
        </div>
      )}

      {/* Mobile Drawer */}
      <Drawer
        title="Filters"
        placement="bottom"
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        height="60vh"
      >
        {filtersUI}
      </Drawer>

      {/* Table */}
      <CompaniesTable
        companiesData={data?.data}
        total={data?.count}
        onChange={onChange}
        isLoading={isLoading}
        isFetching={isFetching}
        skip={skip}
        refetch={refetch}
      />

      {/* Modals */}
      <MultipleEditModal visible={isModalVisible} onClose={() => setIsModalVisible(false)} />
    </div>
  );
};

export default Companies;
